import Plugin from '../core/Plugin';
import * as SelectionUtils from '../SelectionUtils';

/**
 * TablePlugin - Handles table insertion, manipulation, and resizing
 */
class TablePlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'Table';
    this.tableObserver = null;
    this.contextMenu = null;
    this.contextMenuVisible = false;
  }

  init() {
    super.init();
  }

  /**
   * Called after the editor is fully initialized
   */
  afterEditorInit() {
    this.setupTableObserver();
    this.setupContextMenu();
  }

  destroy() {
    if (this.tableObserver) {
      this.tableObserver.disconnect();
      this.tableObserver = null;
    }
    this.destroyContextMenu();
    super.destroy();
  }

  getToolbarButtons() {
    return [
      {
        name: 'insert-table',
        buttons: [
          {
            command: 'insertTable',
            label: '<i class="fa-solid fa-table"></i>',
            title: 'Insert Table',
          },
        ],
      },
    ];
  }

  executeCommand(command) {
    switch (command) {
      case 'insertTable':
        this.showTableModal();
        break;
      case 'addRowBefore':
        this.addTableRow(0);
        break;
      case 'addRowAfter':
        this.addTableRow(1);
        break;
      case 'addColumnBefore':
        this.addTableColumn(0);
        break;
      case 'addColumnAfter':
        this.addTableColumn(1);
        break;
      case 'removeRow':
        this.removeTableRow();
        break;
      case 'removeColumn':
        this.removeTableColumn();
        break;
      case 'removeCell':
        this.removeCell();
        break;
      case 'deleteTable':
        this.deleteTable();
        break;
      case 'toggleBorderTop':
        this.toggleCellBorder('top');
        break;
      case 'toggleBorderRight':
        this.toggleCellBorder('right');
        break;
      case 'toggleBorderBottom':
        this.toggleCellBorder('bottom');
        break;
      case 'toggleBorderLeft':
        this.toggleCellBorder('left');
        break;
      case 'mergeCellLeft':
        this.mergeCellLeft();
        break;
      case 'mergeCellRight':
        this.mergeCellRight();
        break;
      case 'mergeCellTop':
        this.mergeCellTop();
        break;
      case 'mergeCellBottom':
        this.mergeCellBottom();
        break;
    }
  }

  showTableModal() {
    // Save the current selection before showing modal
    const savedSelection = SelectionUtils.saveSelection();

    const overlay = document.createElement('div');
    overlay.className = 'quillkit-dialog-overlay';

    const dialog = document.createElement('div');
    dialog.className = 'quillkit-dialog';

    const header = document.createElement('div');
    header.className = 'quillkit-dialog-header';
    header.textContent = 'Insert Table';

    const body = document.createElement('div');
    body.className = 'quillkit-dialog-body';

    const rowsLabel = document.createElement('label');
    rowsLabel.textContent = 'Rows:';
    rowsLabel.htmlFor = 'quillkit-table-rows';
    const rowsInput = document.createElement('input');
    rowsInput.type = 'number';
    rowsInput.id = 'quillkit-table-rows';
    rowsInput.min = '1';
    rowsInput.max = '20';
    rowsInput.value = '3';

    const colsLabel = document.createElement('label');
    colsLabel.textContent = 'Columns:';
    colsLabel.htmlFor = 'quillkit-table-cols';
    colsLabel.className = 'quillkit-dialog-label-spacing';
    const colsInput = document.createElement('input');
    colsInput.type = 'number';
    colsInput.id = 'quillkit-table-cols';
    colsInput.min = '1';
    colsInput.max = '20';
    colsInput.value = '3';

    const borderColorLabel = document.createElement('label');
    borderColorLabel.textContent = 'Border Color:';
    borderColorLabel.htmlFor = 'quillkit-table-border-color';
    borderColorLabel.className = 'quillkit-dialog-label-spacing';
    const borderColorInput = document.createElement('input');
    borderColorInput.type = 'color';
    borderColorInput.id = 'quillkit-table-border-color';
    borderColorInput.value = '#000000';

    body.appendChild(rowsLabel);
    body.appendChild(rowsInput);
    body.appendChild(colsLabel);
    body.appendChild(colsInput);
    body.appendChild(borderColorLabel);
    body.appendChild(borderColorInput);

    const footer = document.createElement('div');
    footer.className = 'quillkit-dialog-footer';

    const closeModal = () => {
      if (overlay && overlay.parentNode === document.body) {
        document.body.removeChild(overlay);
      }
    };

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'quillkit-editor-button';
    cancelBtn.addEventListener('click', closeModal);

    const insertBtn = document.createElement('button');
    insertBtn.textContent = 'Insert';
    insertBtn.className = 'quillkit-editor-button';
    insertBtn.style.background = '#007bff';
    insertBtn.style.color = 'white';
    insertBtn.addEventListener('click', () => {
      const rows = parseInt(rowsInput.value, 10);
      const cols = parseInt(colsInput.value, 10);
      const borderColor = borderColorInput.value;

      if (rows < 1 || rows > 20 || cols < 1 || cols > 20) {
        if (rows < 1 || rows > 20) {
          rowsInput.style.borderColor = 'red';
        }
        if (cols < 1 || cols > 20) {
          colsInput.style.borderColor = 'red';
        }
        return;
      }

      // Check if there's already a valid selection before focusing
      // This handles cases where a selection was set after the modal opened
      const preSelection = window.getSelection();
      let preSavedSelection = null;
      if (preSelection && preSelection.rangeCount > 0) {
        try {
          const range = preSelection.getRangeAt(0);
          if (this.editor.richEditor.contains(range.commonAncestorContainer)) {
            preSavedSelection = SelectionUtils.saveSelection();
          }
        } catch (e) {
          // Ignore errors
        }
      }

      // Focus the editor
      this.editor.richEditor.focus({ preventScroll: true });

      // Restore selection in priority order:
      // 1. Pre-focus selection (if valid) - handles test cases where selection is set after modal opens
      // 2. Saved selection from when modal opened - handles normal user interaction
      // 3. Create fallback selection at end

      let selectionRestored = false;

      // Try to restore pre-focus selection first
      if (preSavedSelection) {
        try {
          SelectionUtils.restoreSelection(preSavedSelection);
          selectionRestored = true;
        } catch (e) {
          // Continue to next option
        }
      }

      // Try to restore saved selection from modal open
      if (!selectionRestored && savedSelection) {
        try {
          SelectionUtils.restoreSelection(savedSelection);
          selectionRestored = true;
        } catch (e) {
          // Continue to fallback
        }
      }

      // Fallback: create selection at end
      if (!selectionRestored) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.editor.richEditor);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      SelectionUtils.insertTable(rows, cols, borderColor);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
      closeModal();
    });

    footer.appendChild(cancelBtn);
    footer.appendChild(insertBtn);

    dialog.appendChild(header);
    dialog.appendChild(body);
    dialog.appendChild(footer);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    rowsInput.focus();

    const handleKeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        insertBtn.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      }
    };

    rowsInput.addEventListener('keydown', handleKeydown);
    colsInput.addEventListener('keydown', handleKeydown);
    borderColorInput.addEventListener('keydown', handleKeydown);
  }

  addTableRow(position) {
    const table = SelectionUtils.getTableAtSelection();
    if (table) {
      SelectionUtils.addTableRow(table, position);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  addTableColumn(position) {
    const table = SelectionUtils.getTableAtSelection();
    if (table) {
      SelectionUtils.addTableColumn(table, position);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  removeTableRow() {
    const table = SelectionUtils.getTableAtSelection();
    if (table) {
      SelectionUtils.removeTableRow(table);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  removeTableColumn() {
    const table = SelectionUtils.getTableAtSelection();
    if (table) {
      SelectionUtils.removeTableColumn(table);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  removeCell() {
    const table = SelectionUtils.getTableAtSelection();
    if (table) {
      SelectionUtils.removeCell(table);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  deleteTable() {
    const table = SelectionUtils.getTableAtSelection();
    if (table) {
      SelectionUtils.deleteTable(table);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  toggleCellBorder(side) {
    const cell = SelectionUtils.getCellAtSelection();
    if (cell) {
      SelectionUtils.toggleCellBorder(cell, side);
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  mergeCellLeft() {
    const cell = SelectionUtils.getCellAtSelection();
    if (cell) {
      const success = SelectionUtils.mergeCellLeft(cell);
      if (success) {
        this.editor.syncToTextarea();
        this.editor.saveToHistory();
      }
    }
  }

  mergeCellRight() {
    const cell = SelectionUtils.getCellAtSelection();
    if (cell) {
      const success = SelectionUtils.mergeCellRight(cell);
      if (success) {
        this.editor.syncToTextarea();
        this.editor.saveToHistory();
      }
    }
  }

  mergeCellTop() {
    const cell = SelectionUtils.getCellAtSelection();
    if (cell) {
      const success = SelectionUtils.mergeCellTop(cell);
      if (success) {
        this.editor.syncToTextarea();
        this.editor.saveToHistory();
      }
    }
  }

  mergeCellBottom() {
    const cell = SelectionUtils.getCellAtSelection();
    if (cell) {
      const success = SelectionUtils.mergeCellBottom(cell);
      if (success) {
        this.editor.syncToTextarea();
        this.editor.saveToHistory();
      }
    }
  }

  setupTableObserver() {
    this.tableObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'TABLE') {
              this.editor.wrapTableIfNeeded(node);
              this.editor.addResizeHandlesToTable(node);
            } else {
              const tables = node.querySelectorAll ? node.querySelectorAll('table') : [];
              tables.forEach((table) => {
                this.editor.wrapTableIfNeeded(table);
                this.editor.addResizeHandlesToTable(table);
              });
            }
          }
        });
      });
    });

    this.tableObserver.observe(this.editor.richEditor, {
      childList: true,
      subtree: true,
    });

    const existingTables = this.editor.richEditor.querySelectorAll('table');
    existingTables.forEach((table) => {
      this.editor.wrapTableIfNeeded(table);
      this.editor.addResizeHandlesToTable(table);
    });
  }

  /**
   * Setup context menu for tables
   */
  setupContextMenu() {
    // Create context menu element
    this.contextMenu = document.createElement('div');
    this.contextMenu.className = 'quillkit-editor-context-menu';
    document.body.appendChild(this.contextMenu);

    // Add contextmenu event listener to editor
    this.contextMenuHandler = (e) => {
      // Check if right-click is on or inside a table
      const table = this.getTableFromEvent(e);
      if (table) {
        e.preventDefault();
        this.showContextMenu(e);
      } else {
        this.hideContextMenu();
      }
    };

    this.editor.richEditor.addEventListener('contextmenu', this.contextMenuHandler);

    // Hide context menu on click outside
    this.documentClickHandler = (e) => {
      if (this.contextMenuVisible && !this.contextMenu.contains(e.target)) {
        this.hideContextMenu();
      }
    };
    document.addEventListener('click', this.documentClickHandler);

    // Hide context menu on escape key
    this.documentKeyHandler = (e) => {
      if (e.key === 'Escape' && this.contextMenuVisible) {
        this.hideContextMenu();
      }
    };
    document.addEventListener('keydown', this.documentKeyHandler);

    // Hide context menu on scroll
    this.scrollHandler = () => {
      if (this.contextMenuVisible) {
        this.hideContextMenu();
      }
    };
    this.editor.richEditor.addEventListener('scroll', this.scrollHandler);
  }

  /**
   * Destroy context menu and cleanup event listeners
   */
  destroyContextMenu() {
    if (this.contextMenu && this.contextMenu.parentNode) {
      this.contextMenu.parentNode.removeChild(this.contextMenu);
    }
    this.contextMenu = null;

    if (this.contextMenuHandler) {
      this.editor.richEditor.removeEventListener('contextmenu', this.contextMenuHandler);
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    if (this.documentKeyHandler) {
      document.removeEventListener('keydown', this.documentKeyHandler);
    }
    if (this.scrollHandler) {
      this.editor.richEditor.removeEventListener('scroll', this.scrollHandler);
    }
  }

  /**
   * Get table element from event target
   */
  getTableFromEvent(e) {
    let element = e.target;
    while (element && element !== this.editor.richEditor) {
      if (element.tagName === 'TABLE') {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  /**
   * Get context menu items for tables
   */
  getContextMenuItems() {
    return [
      {
        label: 'Add Row Above',
        icon: '↑+',
        command: 'addRowBefore',
      },
      {
        label: 'Add Row Below',
        icon: '↓+',
        command: 'addRowAfter',
      },
      {
        label: 'Add Column Left',
        icon: '←+',
        command: 'addColumnBefore',
      },
      {
        label: 'Add Column Right',
        icon: '→+',
        command: 'addColumnAfter',
      },
      { separator: true },
      {
        label: 'Remove Row',
        icon: '↕−',
        command: 'removeRow',
      },
      {
        label: 'Remove Column',
        icon: '↔−',
        command: 'removeColumn',
      },
      {
        label: 'Remove Cell',
        icon: '⊟',
        command: 'removeCell',
      },
      { separator: true },
      {
        label: 'Merge Cell Left',
        icon: '←⊔',
        command: 'mergeCellLeft',
      },
      {
        label: 'Merge Cell Right',
        icon: '⊔→',
        command: 'mergeCellRight',
      },
      {
        label: 'Merge Cell Up',
        icon: '↑⊔',
        command: 'mergeCellTop',
      },
      {
        label: 'Merge Cell Down',
        icon: '⊔↓',
        command: 'mergeCellBottom',
      },
      { separator: true },
      {
        label: 'Toggle Top Border',
        icon: '⊤',
        command: 'toggleBorderTop',
      },
      {
        label: 'Toggle Right Border',
        icon: '⊣',
        command: 'toggleBorderRight',
      },
      {
        label: 'Toggle Bottom Border',
        icon: '⊥',
        command: 'toggleBorderBottom',
      },
      {
        label: 'Toggle Left Border',
        icon: '⊢',
        command: 'toggleBorderLeft',
      },
      { separator: true },
      {
        label: 'Delete Table',
        icon: '🗑',
        command: 'deleteTable',
      },
    ];
  }

  /**
   * Show context menu at mouse position
   */
  showContextMenu(e) {
    // Clear existing menu items
    this.contextMenu.innerHTML = '';

    // Get menu items
    const items = this.getContextMenuItems();

    // Create menu items
    items.forEach((item) => {
      if (item.separator) {
        const separator = document.createElement('div');
        separator.className = 'quillkit-editor-context-menu-separator';
        this.contextMenu.appendChild(separator);
      } else {
        const menuItem = document.createElement('button');
        menuItem.className = 'quillkit-editor-context-menu-item';

        const icon = document.createElement('span');
        icon.className = 'quillkit-editor-context-menu-icon';
        icon.textContent = item.icon;

        const label = document.createElement('span');
        label.className = 'quillkit-editor-context-menu-label';
        label.textContent = item.label;

        menuItem.appendChild(icon);
        menuItem.appendChild(label);

        menuItem.addEventListener('click', () => {
          this.executeCommand(item.command);
          this.hideContextMenu();
        });

        this.contextMenu.appendChild(menuItem);
      }
    });

    // Position the menu at mouse position
    this.contextMenu.style.left = `${e.pageX}px`;
    this.contextMenu.style.top = `${e.pageY}px`;
    this.contextMenu.classList.add('show');
    this.contextMenuVisible = true;

    // Adjust position if menu goes off-screen
    setTimeout(() => {
      const rect = this.contextMenu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (rect.right > viewportWidth) {
        this.contextMenu.style.left = `${e.pageX - rect.width}px`;
      }
      if (rect.bottom > viewportHeight) {
        this.contextMenu.style.top = `${e.pageY - rect.height}px`;
      }
    }, 0);
  }

  /**
   * Hide context menu
   */
  hideContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.classList.remove('show');
      this.contextMenuVisible = false;
    }
  }

  getMetadata() {
    return {
      name: 'Tables',
      version: '1.0.0',
      description: 'Provides table insertion, manipulation, and resizing functionality',
      author: 'AtlaraFirm',
    };
  }
}

export default TablePlugin;
